import { Box, Button, Flex, Heading, Spacer, Text } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { NavLink } from "react-router-dom";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { RequireUserPermissions } from "src/components/Permissions/RequireUserPermissions";
import { Glossary } from "src/components/Text/Glossary";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import { ParentDetailsForm } from "./components/ParentDetailsForm";
import { ParentStudentSection } from "./components/ParentStudentSection";
import { PossibleParentDuplicatesPanel } from "./components/PossibleParentDuplicatesPanel";
import { SelectedParentChip } from "./components/SelectedParentChip";
import { ParentProfileFormType, ParentProfileValidator } from "./schemas";
import { useNewParent } from "./useNewParent";

export const NewParent = () => {
  const {
    organization,
    organizationId,
    canCreateUser,
    initialValues,
    isSubmitting,
    onSubmit,
    cancelHref,
    students,
    selectedExisting,
    onSelectExisting,
    onClearExisting,
  } = useNewParent();

  // An open (unconfirmed) new student sub-form must be added or cancelled before
  // the family can be saved.
  const hasOpenStudentDraft = students.entries.some(
    (entry) => entry.kind === "new" && !entry.confirmed
  );

  if (!canCreateUser) {
    return <NotFound></NotFound>;
  }

  return (
    <RequireUserPermissions permission={"user:create"}>
      <Flex direction="column" minHeight="100%">
        <Breadcrumb
          items={breadcrumb.parent.getBreadcrumbsForNew(organization)}
          mb={8}
        />
        <Formik<ParentProfileFormType>
          initialValues={initialValues}
          onSubmit={onSubmit}
          // When linking to an existing parent the profile form is hidden, so its
          // (now irrelevant) values must not block submit.
          validate={
            selectedExisting
              ? undefined
              : validateWithZod(ParentProfileValidator)
          }
          // Validate on mount so the Create button reflects validity immediately,
          // not only after the first edit or a submit attempt.
          validateOnMount
        >
          {({ isValid }) => (
            <Flex as={Form} direction="column" gap={6} flexGrow="1">
              <Heading as="h1" fontSize="2xl" fontWeight="600">
                <Glossary>Create parent/guardian</Glossary>
              </Heading>
              <Flex
                direction={{ base: "column", lg: "row" }}
                gap={6}
                align="flex-start"
                width="100%"
              >
                <Flex direction="column" gap={6} flex="2" width="100%">
                  <Box
                    width="100%"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    padding={5}
                  >
                    <Text fontSize="lg" fontWeight="700">
                      <Glossary>Parent/guardian</Glossary>
                    </Text>
                    <Text fontSize="sm" color="gray.500" marginBottom={4}>
                      <Glossary>
                        {selectedExisting
                          ? "Using an existing parent/guardian. Students below will be linked to them."
                          : "Parent/guardians are checked against contact methods."}
                      </Glossary>
                    </Text>
                    {selectedExisting ? (
                      <SelectedParentChip
                        parent={selectedExisting}
                        onClear={onClearExisting}
                      />
                    ) : (
                      <ParentDetailsForm requiredIndicators />
                    )}
                  </Box>
                  <Box
                    width="100%"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    padding={5}
                  >
                    <Text fontSize="lg" fontWeight="700">
                      <Glossary>Student</Glossary>
                    </Text>
                    <Text fontSize="sm" color="gray.500" marginBottom={4}>
                      <Glossary>
                        Students are checked against date of birth once they're
                        entered.
                      </Glossary>
                    </Text>
                    <ParentStudentSection
                      organizationId={organizationId}
                      entries={students.entries}
                      onAddExisting={students.addExisting}
                      onAddDraft={students.addDraft}
                      onUpdateDraft={students.updateDraft}
                      onConfirmDraft={students.confirmDraft}
                      onRemove={students.remove}
                      // Hide students already linked to the selected existing
                      // parent so we never attempt a duplicate relationship.
                      excludeIds={selectedExisting?.children.map((c) => c.id)}
                    />
                  </Box>
                </Flex>
                {!selectedExisting && (
                  <Box flex="1" width="100%">
                    <PossibleParentDuplicatesPanel
                      organizationId={organizationId}
                      onUseParent={onSelectExisting}
                    />
                  </Box>
                )}
              </Flex>
              <Spacer />
              <AdminFormButtons>
                <Button
                  as={NavLink}
                  to={cancelHref}
                  isLoading={organization.isLoading()}
                  variant="outline"
                  colorScheme="gray"
                >
                  Cancel
                </Button>
                <Spacer />
                <Button
                  type="submit"
                  marginLeft={4}
                  isLoading={isSubmitting}
                  // Linking to an existing parent: the hidden form's validity is
                  // irrelevant, so gate only on having at least one student.
                  // Creating a new parent: gate on the form being valid.
                  isDisabled={
                    hasOpenStudentDraft ||
                    (selectedExisting != null
                      ? students.entries.length === 0
                      : !isValid)
                  }
                >
                  <Glossary>Save and create family</Glossary>
                </Button>
              </AdminFormButtons>
            </Flex>
          )}
        </Formik>
      </Flex>
    </RequireUserPermissions>
  );
};
